import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFileExcel,
    faTimesCircle,
    faTrophy,
    faChevronDown,
    faChevronUp,
    faSearch
} from '@fortawesome/free-solid-svg-icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import api from '../../Api';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const ProductivityStats = () => {
    const [productivityStats, setProductivityStats] = useState(null);
    const [productivityStatsLoading, setProductivityStatsLoading] = useState(false);
    const [selectedSurveyForStats, setSelectedSurveyForStats] = useState(null);
    const [availableSurveys, setAvailableSurveys] = useState([]);
    const [showResumeData, setShowResumeData] = useState(false);
    const [showRawData, setShowRawData] = useState(false);
    const [resumeSearchTerm, setResumeSearchTerm] = useState('');
    const [rawDataSearchTerm, setRawDataSearchTerm] = useState('');
    const [resumeSearchColumn, setResumeSearchColumn] = useState('all');
    const [rawDataSearchColumn, setRawDataSearchColumn] = useState('all');
    const [resumeFilteredData, setResumeFilteredData] = useState(null);
    const [rawDataFilteredData, setRawDataFilteredData] = useState(null);

    useEffect(() => {
        fetchAvailableSurveys();
    }, []);

    useEffect(() => {
        fetchProductivityStats();
    }, [selectedSurveyForStats]);

    useEffect(() => {
        // Initialize filtered data with all data when stats are loaded
        if (productivityStats) {
            if (productivityStats.resume_data) {
                setResumeFilteredData(productivityStats.resume_data);
            }
            if (productivityStats.raw_data) {
                setRawDataFilteredData(productivityStats.raw_data);
            }
        }
    }, [productivityStats]);

    const fetchAvailableSurveys = async () => {
        try {
            const response = await api.get('/cati/surveys').catch(() => ({ data: { data: [] } }));
            const surveys = response.data.data || response.data || [];
            setAvailableSurveys(Array.isArray(surveys) ? surveys : []);
            setSelectedSurveyForStats(null);
        } catch (error) {
            console.error('Error fetching surveys:', error);
        }
    };

    const fetchProductivityStats = async () => {
        try {
            setProductivityStatsLoading(true);
            const params = {};
            if (selectedSurveyForStats !== null && selectedSurveyForStats !== '') {
                params.survey_id = selectedSurveyForStats;
            }
            const response = await api.get('/cati/productivity/stats', { params });
            if (response.data.success) {
                setProductivityStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching productivity statistics:', error);
        } finally {
            setProductivityStatsLoading(false);
        }
    };

    const handleResumeSearch = () => {
        if (!productivityStats || !productivityStats.resume_data) return;
        
        if (!resumeSearchTerm.trim()) {
            setResumeFilteredData(productivityStats.resume_data);
            return;
        }

        const searchLower = resumeSearchTerm.toLowerCase();
        const filtered = productivityStats.resume_data.filter((row) => {
            if (resumeSearchColumn === 'all') {
                return Object.values(row).some(value => 
                    value !== null && value !== undefined && 
                    String(value).toLowerCase().includes(searchLower)
                );
            } else {
                const value = row[resumeSearchColumn];
                return value !== null && value !== undefined && 
                    String(value).toLowerCase().includes(searchLower);
            }
        });
        setResumeFilteredData(filtered);
    };

    const handleRawDataSearch = () => {
        if (!productivityStats || !productivityStats.raw_data) return;
        
        if (!rawDataSearchTerm.trim()) {
            setRawDataFilteredData(productivityStats.raw_data);
            return;
        }

        const searchLower = rawDataSearchTerm.toLowerCase();
        const filtered = productivityStats.raw_data.filter((row) => {
            if (rawDataSearchColumn === 'all') {
                return Object.values(row).some(value => 
                    value !== null && value !== undefined && 
                    String(value).toLowerCase().includes(searchLower)
                );
            } else {
                const value = row[rawDataSearchColumn];
                return value !== null && value !== undefined && 
                    String(value).toLowerCase().includes(searchLower);
            }
        });
        setRawDataFilteredData(filtered);
    };

    const handleExportStats = async () => {
        try {
            const params = {};
            if (selectedSurveyForStats !== null && selectedSurveyForStats !== '') {
                params.survey_id = selectedSurveyForStats;
            }
            
            const response = await api.get('/cati/productivity/stats/export', {
                params,
                responseType: 'blob',
            });

            const contentDisposition = response.headers['content-disposition'];
            let filename = 'statistiques_productivite.xlsx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting productivity statistics:', error);
            alert('Erreur lors de l\'exportation. Veuillez réessayer.');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>

            <div className="relative z-10 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
                            Statistiques de Productivité
                        </h1>
                        <p className="text-gray-600">Vue d'ensemble des statistiques de productivité des enquêteurs</p>
                    </div>

                    {/* Survey Filter and Export Button */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Filtrer par Enquête
                                </label>
                                <select
                                    value={selectedSurveyForStats !== null ? selectedSurveyForStats : ''}
                                    onChange={(e) => setSelectedSurveyForStats(e.target.value ? parseInt(e.target.value) : null)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none w-full sm:w-auto"
                                >
                                    <option value="">Toutes les enquêtes</option>
                                    {availableSurveys.map(survey => (
                                        <option key={survey.id} value={survey.id}>
                                            {survey.title || survey.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={handleExportStats}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 w-full sm:w-auto"
                                title="Exporter en Excel"
                            >
                                <FontAwesomeIcon icon={faFileExcel} />
                                <span>Exporter en Excel</span>
                            </button>
                        </div>
                    </div>

                    {/* Statistics Content */}
                    {productivityStatsLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
                        </div>
                    ) : productivityStats ? (
                        <div className="space-y-6">
                            {/* Points Saillants */}
                            <div className="bg-gray-50 rounded-lg p-6">
                                <h4 className="text-xl font-bold text-gray-900 mb-4">Points Saillants</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div className="bg-white rounded-lg p-4 shadow-sm">
                                        <p className="text-sm text-gray-600 mb-1">Total Postes</p>
                                        <p className="text-2xl font-bold text-gray-900">{productivityStats.highlights?.total_postes || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 shadow-sm">
                                        <p className="text-sm text-gray-600 mb-1">Total Soumissions Valides</p>
                                        <p className="text-2xl font-bold text-green-600">{productivityStats.highlights?.total_submissions || 0}</p>
                                    </div>
                                </div>
                                
                                {/* Most and Least Productive Interviewers */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {productivityStats.highlights?.most_productive_interviewer && (
                                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                            <p className="text-sm font-semibold text-green-800 mb-2">Enquêteur le Plus Productif</p>
                                            <p className="text-lg font-bold text-green-900">{productivityStats.highlights.most_productive_interviewer.interviewer_name}</p>
                                            <p className="text-sm text-green-700">({productivityStats.highlights.most_productive_interviewer.interviewer_ref})</p>
                                            <p className="text-sm text-gray-600 mt-2">
                                                Poste {productivityStats.highlights.most_productive_interviewer.poste_number}
                                            </p>
                                            <p className="text-sm font-semibold text-green-800 mt-1">
                                                Soumissions: {productivityStats.highlights.most_productive_interviewer.submissions_count}
                                            </p>
                                        </div>
                                    )}
                                    {productivityStats.highlights?.least_productive_interviewer && (
                                        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                            <p className="text-sm font-semibold text-red-800 mb-2">Enquêteur le Moins Productif</p>
                                            <p className="text-lg font-bold text-red-900">{productivityStats.highlights.least_productive_interviewer.interviewer_name}</p>
                                            <p className="text-sm text-red-700">({productivityStats.highlights.least_productive_interviewer.interviewer_ref})</p>
                                            <p className="text-sm text-gray-600 mt-2">
                                                Poste {productivityStats.highlights.least_productive_interviewer.poste_number}
                                            </p>
                                            <p className="text-sm font-semibold text-red-800 mt-1">
                                                Soumissions: {productivityStats.highlights.least_productive_interviewer.submissions_count}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Top 10 Interviewers */}
                            {productivityStats.top_interviewers && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                                    {/* Top 10 Best */}
                                    <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
                                        <h4 className="text-xl sm:text-2xl font-bold text-green-600 mb-4 sm:mb-6 flex items-center">
                                            <FontAwesomeIcon icon={faTrophy} className="mr-2 sm:mr-3 text-green-600" />
                                            <span className="text-base sm:text-2xl">Top 10 Plus Productifs</span>
                                        </h4>
                                        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                                            {productivityStats.top_interviewers.best && productivityStats.top_interviewers.best.length > 0 ? (
                                                productivityStats.top_interviewers.best.map((interviewer, index) => (
                                                    <div 
                                                        key={interviewer.id} 
                                                        className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-200"
                                                        style={{ overflow: 'visible' }}
                                                    >
                                                        <div className="p-3 sm:p-4">
                                                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                                                                {/* Rank Number */}
                                                                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                                                                    <span className="text-lg sm:text-xl font-bold text-gray-400">
                                                                        {index + 1}
                                                                    </span>
                                                                </div>
                                                                
                                                                {/* Info Section */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex items-center gap-2 sm:gap-3">
                                                                                {/* Avatar/Initials Circle */}
                                                                                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center">
                                                                                    <span className="text-green-600 font-bold text-sm sm:text-base">
                                                                                        {interviewer.name ? interviewer.name.charAt(0).toUpperCase() : '?'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="min-w-0 flex-1">
                                                                                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                                                                                        {interviewer.name || 'N/A'}
                                                                                    </h3>
                                                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1">
                                                                                        <span className="text-xs text-gray-500">Ref: {interviewer.ref || 'N/A'}</span>
                                                                                        {interviewer.poste_number && (
                                                                                            <span className="text-xs text-gray-500">• Poste {interviewer.poste_number}</span>
                                                                                        )}
                                                                                    </div>
                                                                                    {interviewer.posts && interviewer.posts.length > 0 && (
                                                                                        <div className="text-xs text-gray-500 mt-1 break-words">
                                                                                            Postes: {interviewer.posts.map(p => p.number).join(', ')}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Score */}
                                                                        <div className="flex sm:flex-col sm:items-end sm:text-right gap-2 sm:gap-1">
                                                                            <div className="text-xl sm:text-2xl font-bold text-green-600">
                                                                                {interviewer.total_submissions || 0}
                                                                            </div>
                                                                            <div className="text-xs text-gray-500">
                                                                                soumission{interviewer.total_submissions !== 1 ? 's' : ''}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center py-12">
                                                    <p className="text-gray-500">Aucune donnée disponible</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Top 10 Worst */}
                                    <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
                                        <h4 className="text-xl sm:text-2xl font-bold text-red-600 mb-4 sm:mb-6 flex items-center">
                                            <FontAwesomeIcon icon={faTimesCircle} className="mr-2 sm:mr-3" />
                                            <span className="text-base sm:text-2xl">Top 10 Moins Productifs</span>
                                        </h4>
                                        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                                            {productivityStats.top_interviewers.worst && productivityStats.top_interviewers.worst.length > 0 ? (
                                                productivityStats.top_interviewers.worst.map((interviewer, index) => (
                                                    <div 
                                                        key={interviewer.id} 
                                                        className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-200"
                                                        style={{ overflow: 'visible' }}
                                                    >
                                                        <div className="p-3 sm:p-4">
                                                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                                                                {/* Rank Number */}
                                                                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                                                                    <span className="text-lg sm:text-xl font-bold text-gray-400">
                                                                        {index + 1}
                                                                    </span>
                                                                </div>
                                                                
                                                                {/* Info Section */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex items-center gap-2 sm:gap-3">
                                                                                {/* Avatar/Initials Circle */}
                                                                                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-100 flex items-center justify-center">
                                                                                    <span className="text-red-600 font-bold text-sm sm:text-base">
                                                                                        {interviewer.name ? interviewer.name.charAt(0).toUpperCase() : '?'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="min-w-0 flex-1">
                                                                                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                                                                                        {interviewer.name || 'N/A'}
                                                                                    </h3>
                                                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1">
                                                                                        <span className="text-xs text-gray-500">Ref: {interviewer.ref || 'N/A'}</span>
                                                                                        {interviewer.poste_number && (
                                                                                            <span className="text-xs text-gray-500">• Poste {interviewer.poste_number}</span>
                                                                                        )}
                                                                                    </div>
                                                                                    {interviewer.posts && interviewer.posts.length > 0 && (
                                                                                        <div className="text-xs text-gray-500 mt-1 break-words">
                                                                                            Postes: {interviewer.posts.map(p => p.number).join(', ')}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Score */}
                                                                        <div className="flex sm:flex-col sm:items-end sm:text-right gap-2 sm:gap-1">
                                                                            <div className="text-xl sm:text-2xl font-bold text-red-600">
                                                                                {interviewer.total_submissions || 0}
                                                                            </div>
                                                                            <div className="text-xs text-gray-500">
                                                                                soumission{interviewer.total_submissions !== 1 ? 's' : ''}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center py-12">
                                                    <p className="text-gray-500">Aucune donnée disponible</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Résumé par Enquêteur */}
                            {productivityStats.resume_data && productivityStats.resume_data.length > 0 && (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                    <button
                                        onClick={() => setShowResumeData(!showResumeData)}
                                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        <h4 className="text-xl font-bold text-gray-900">Résumé par Enquêteur</h4>
                                        <FontAwesomeIcon 
                                            icon={showResumeData ? faChevronUp : faChevronDown} 
                                            className="text-gray-500 transition-transform duration-200"
                                        />
                                    </button>
                                    
                                    {showResumeData && (
                                        <div className="px-6 pb-6 border-t border-gray-100">
                                            {/* Search Input with Column Filter */}
                                            <div className="mt-4 mb-4 flex flex-col sm:flex-row gap-2">
                                                <select
                                                    value={resumeSearchColumn}
                                                    onChange={(e) => setResumeSearchColumn(e.target.value)}
                                                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                                >
                                                    <option value="all">Toutes les colonnes</option>
                                                    <option value="Projet">Questionnaire</option>
                                                    <option value="ID Projet">ID LimeSurvey</option>
                                                    <option value="Enquêteur">Enquêteur</option>
                                                    <option value="Réf. Enquêteur">Réf. Enquêteur</option>
                                                    <option value="Poste">Poste</option>
                                                    <option value="Nombre_Enregistrements">Nb Enregistrements</option>
                                                    <option value="Premiere_Date_Soumission">Première Date</option>
                                                    <option value="Derniere_Date_Soumission">Dernière Date</option>
                                                </select>
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher..."
                                                    value={resumeSearchTerm}
                                                    onChange={(e) => setResumeSearchTerm(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleResumeSearch();
                                                        }
                                                    }}
                                                    className="flex-1 w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                                />
                                                <button
                                                    onClick={handleResumeSearch}
                                                    className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm whitespace-nowrap"
                                                >
                                                    <FontAwesomeIcon icon={faSearch} />
                                                    <span>Rechercher</span>
                                                </button>
                                            </div>
                                            
                                            <div className="overflow-x-auto mt-4">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-red-600">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Questionnaire</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID LimeSurvey</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Enquêteur</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Réf. Enquêteur</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Poste</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Nb Enregistrements</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Première Date</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Dernière Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {(resumeFilteredData || productivityStats.resume_data || []).map((row, index) => (
                                                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-red-50'}>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Projet || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['ID Projet'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Enquêteur || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Réf. Enquêteur'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Poste || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Nombre_Enregistrements || 0}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Premiere_Date_Soumission || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Derniere_Date_Soumission || 'N/A'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Données Brutes */}
                            {productivityStats.raw_data && productivityStats.raw_data.length > 0 && (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                    <button
                                        onClick={() => setShowRawData(!showRawData)}
                                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        <h4 className="text-xl font-bold text-gray-900">Données Brutes</h4>
                                        <FontAwesomeIcon 
                                            icon={showRawData ? faChevronUp : faChevronDown} 
                                            className="text-gray-500 transition-transform duration-200"
                                        />
                                    </button>
                                    
                                    {showRawData && (
                                        <div className="px-6 pb-6 border-t border-gray-100">
                                            {/* Search Input with Column Filter */}
                                            <div className="mt-4 mb-4 flex flex-col sm:flex-row gap-2">
                                                <select
                                                    value={rawDataSearchColumn}
                                                    onChange={(e) => setRawDataSearchColumn(e.target.value)}
                                                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                                >
                                                    <option value="all">Toutes les colonnes</option>
                                                    <option value="Projet">Questionnaire</option>
                                                    <option value="ID LimeSurvey">ID LimeSurvey</option>
                                                    <option value="Poste">Poste</option>
                                                    <option value="Enquêteur">Enquêteur</option>
                                                    <option value="Réf. Enquêteur">Réf. Enquêteur</option>
                                                    <option value="Status">Status</option>
                                                    <option value="TEL">TEL</option>
                                                    <option value="Date de soumission">Date Soumission</option>
                                                    <option value="ID de la réponse">ID Réponse</option>
                                                </select>
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher..."
                                                    value={rawDataSearchTerm}
                                                    onChange={(e) => setRawDataSearchTerm(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleRawDataSearch();
                                                        }
                                                    }}
                                                    className="flex-1 w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                                />
                                                <button
                                                    onClick={handleRawDataSearch}
                                                    className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm whitespace-nowrap"
                                                >
                                                    <FontAwesomeIcon icon={faSearch} />
                                                    <span>Rechercher</span>
                                                </button>
                                            </div>
                                            
                                            <div className="overflow-x-auto mt-4">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-red-600">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Questionnaire</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID LimeSurvey</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Poste</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Enquêteur</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Réf. Enquêteur</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Status</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">TEL</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Date Soumission</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID Réponse</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Lien LimeSurvey</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {(rawDataFilteredData || productivityStats.raw_data || []).map((row, index) => (
                                                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-red-50'}>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Projet || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['ID LimeSurvey'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Poste || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Enquêteur || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Réf. Enquêteur'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Status || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.TEL || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Date de soumission'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['ID de la réponse'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                                    {row['Lien LimeSurvey'] && row['Lien LimeSurvey'] !== 'N/A' ? (
                                                                        <a 
                                                                            href={row['Lien LimeSurvey']} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="text-red-600 hover:text-red-800 underline"
                                                                        >
                                                                            Ouvrir
                                                                        </a>
                                                                    ) : (
                                                                        <span className="text-gray-400">N/A</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            Aucune donnée disponible
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductivityStats;

